'use client'

import { useCallback } from 'react'
import { useWorkflow } from '@/context/workflow-context-simple'
import { useSaveCurrentViewState } from '@/hooks/use-save-current-view-state'

/**
 * Appends a hidden column's fieldConfigurationId to the end of the current
 * stage's visible field list and persists the change via the shared
 * `useSaveCurrentViewState` flow. Used by the Jump-to-Column command palette
 * when the user picks a hidden column.
 *
 * Returns true on success (or when the column was already visible — no-op).
 * Errors are toasted by the underlying hook; this returns false in that case
 * so callers can roll back any optimistic UI state.
 */
export function useAddColumnToView() {
  const { configuration, currentStage } = useWorkflow()
  const saveCurrentViewState = useSaveCurrentViewState()

  return useCallback(
    async (fieldConfigurationId: string): Promise<boolean> => {
      if (!configuration || !currentStage) return false

      const currentStageConfig = configuration.stageConfigurations.find(
        s => s.stageCode.toLowerCase() === currentStage.toLowerCase()
      )
      if (!currentStageConfig) return false

      const currentFieldIds = currentStageConfig.fieldConfigurations.map(f => f.fieldConfigurationId)
      if (currentFieldIds.includes(fieldConfigurationId)) {
        // Already visible — nothing to persist.
        return true
      }

      // Append the new id to the current stage; leave other stages unchanged.
      const newFieldIds = [...currentFieldIds, fieldConfigurationId]
      const stageConfigurations = configuration.stageConfigurations.map(stage =>
        stage.stageId === currentStageConfig.stageId
          ? { stageConfigurationId: stage.stageId, fieldConfigurationIds: newFieldIds }
          : {
              stageConfigurationId: stage.stageId,
              fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
            }
      )

      return saveCurrentViewState({
        stageConfigurations,
        optimisticOrderedFieldIds: newFieldIds,
      })
    },
    [configuration, currentStage, saveCurrentViewState]
  )
}
